import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Extend jsPDF type to include autoTable and lastAutoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
    lastAutoTable?: {
      finalY: number;
    };
  }
}

// Export formats
export type ExportFormat = 'pdf' | 'excel' | 'csv';

// Report data interfaces
interface ReportData {
  title: string;
  period: string;
  data: any;
  type: 'profit-loss' | 'balance-sheet' | 'cash-flow' | 'aged-debtors';
}

// PDF Export Functions
export const exportToPDF = (reportData: ReportData): void => {
  try {
    console.log('Starting PDF export...', reportData);

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;

    // Header
    doc.setFontSize(20);
    doc.text(reportData.title, pageWidth / 2, 20, { align: 'center' });

    doc.setFontSize(12);
    doc.text(reportData.period, pageWidth / 2, 30, { align: 'center' });

    let yPosition = 50;

    console.log('Processing report type:', reportData.type);

    switch (reportData.type) {
      case 'profit-loss':
        yPosition = exportProfitLossToPDF(doc, reportData.data, yPosition);
        break;
      case 'balance-sheet':
        yPosition = exportBalanceSheetToPDF(doc, reportData.data, yPosition);
        break;
      case 'cash-flow':
        yPosition = exportCashFlowToPDF(doc, reportData.data, yPosition);
        break;
      case 'aged-debtors':
        yPosition = exportAgedDebtorsToPDF(doc, reportData.data, yPosition);
        break;
      default:
        console.error('Unknown report type:', reportData.type);
        throw new Error(`Unknown report type: ${reportData.type}`);
    }

    console.log('Adding footer...');

    // Footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(
        `Generated on ${new Date().toLocaleDateString('en-US')} - Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }

    // Save the PDF
    const fileName = `${reportData.title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    console.log('Saving PDF as:', fileName);
    doc.save(fileName);
    console.log('PDF export completed successfully');

  } catch (error) {
    console.error('PDF export failed:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    alert(`PDF export failed: ${errorMessage}`);
    throw error;
  }
};

// Profit & Loss PDF Export
const exportProfitLossToPDF = (doc: jsPDF, data: any, startY: number): number => {
  try {
    console.log('Exporting Profit & Loss to PDF...', data);

    const tableData = [
      ['REVENUE', '', ''],
      ['Sales Revenue', data.revenue?.sales_revenue_formatted || '$0.00', ''],
      ['Other Income', data.revenue?.other_income_formatted || '$0.00', ''],
      ['Total Revenue', data.revenue?.total_revenue_formatted || '$0.00', ''],
      ['', '', ''],
      ['EXPENSES', '', ''],
      ['Cost of Sales', data.expenses?.cost_of_sales_formatted || '$0.00', ''],
      ['Operating Expenses', data.expenses?.operating_expenses_formatted || '$0.00', ''],
      ['Administrative Expenses', data.expenses?.administrative_expenses_formatted || '$0.00', ''],
      ['Total Expenses', data.expenses?.total_expenses_formatted || '$0.00', ''],
      ['', '', ''],
      ['PROFIT SUMMARY', '', ''],
      ['Gross Profit', data.gross_profit_formatted || '$0.00', ''],
      ['Net Profit', data.net_profit_formatted || '$0.00', ''],
      ['Gross Margin', `${data.gross_margin?.toFixed(1) || '0.0'}%`, ''],
      ['Net Margin', `${data.net_margin?.toFixed(1) || '0.0'}%`, '']
    ];

    console.log('Creating autoTable...');

    // Check if autoTable is available
    if (typeof doc.autoTable !== 'function') {
      console.error('autoTable is not available on jsPDF instance');
      // Fallback to basic text
      let yPos = startY;
      doc.setFontSize(12);
      doc.text('PROFIT & LOSS REPORT', 20, yPos);
      yPos += 10;
      doc.setFontSize(10);
      doc.text(`Total Revenue: ${data.revenue?.total_revenue_formatted || '$0.00'}`, 20, yPos);
      yPos += 10;
      doc.text(`Total Expenses: ${data.expenses?.total_expenses_formatted || '$0.00'}`, 20, yPos);
      yPos += 10;
      doc.text(`Net Profit: ${data.net_profit_formatted || '$0.00'}`, 20, yPos);
      return yPos + 20;
    }

    doc.autoTable({
      startY: startY,
      head: [['Description', 'Amount', 'Notes']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [41, 128, 185] },
      styles: { fontSize: 10 }
    });

    console.log('Profit & Loss PDF table created successfully');
  } catch (error) {
    console.error('Error in exportProfitLossToPDF:', error);
    throw error;
  }

  return doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 20 : startY + 100;
};

// Balance Sheet PDF Export
const exportBalanceSheetToPDF = (doc: jsPDF, data: any, startY: number): number => {
  const tableData = [
    ['ASSETS', '', ''],
    ['Current Assets:', '', ''],
    ['Cash and Bank', data.current_assets?.cash_and_bank_formatted || '$0.00', ''],
    ['Accounts Receivable', data.current_assets?.accounts_receivable_formatted || '$0.00', ''],
    ['Inventory', data.current_assets?.inventory_formatted || '$0.00', ''],
    ['Total Current Assets', data.current_assets?.total_current_assets_formatted || '$0.00', ''],
    ['', '', ''],
    ['Fixed Assets:', '', ''],
    ['Property, Plant & Equipment', data.fixed_assets?.property_plant_equipment_formatted || '$0.00', ''],
    ['Less: Accumulated Depreciation', data.fixed_assets?.accumulated_depreciation_formatted || '$0.00', ''],
    ['Total Fixed Assets', data.fixed_assets?.total_fixed_assets_formatted || '$0.00', ''],
    ['TOTAL ASSETS', data.total_assets_formatted || '$0.00', ''],
    ['', '', ''],
    ['LIABILITIES & EQUITY', '', ''],
    ['Current Liabilities:', '', ''],
    ['Accounts Payable', data.current_liabilities?.accounts_payable_formatted || '$0.00', ''],
    ['Accrued Expenses', data.current_liabilities?.accrued_expenses_formatted || '$0.00', ''],
    ['Total Current Liabilities', data.current_liabilities?.total_current_liabilities_formatted || '$0.00', ''],
    ['', '', ''],
    ['Long-term Liabilities:', '', ''],
    ['Long-term Debt', data.long_term_liabilities?.long_term_debt_formatted || '$0.00', ''],
    ['Total Liabilities', data.total_liabilities_formatted || '$0.00', ''],
    ['', '', ''],
    ['Equity:', '', ''],
    ['Share Capital', data.equity?.share_capital_formatted || '$0.00', ''],
    ['Retained Earnings', data.equity?.retained_earnings_formatted || '$0.00', ''],
    ['Total Equity', data.equity?.total_equity_formatted || '$0.00', ''],
    ['TOTAL LIABILITIES & EQUITY', data.total_liabilities_and_equity_formatted || '$0.00', '']
  ];

  doc.autoTable({
    startY: startY,
    head: [['Description', 'Amount', 'Notes']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [41, 128, 185] },
    styles: { fontSize: 10 }
  });

  return doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 20 : startY + 100;
};

// Cash Flow PDF Export
const exportCashFlowToPDF = (doc: jsPDF, data: any, startY: number): number => {
  const tableData = [
    ['OPERATING ACTIVITIES', '', ''],
    ['Cash from Customers', data.operating_activities?.cash_from_customers_formatted || '$0.00', ''],
    ['Cash to Suppliers', data.operating_activities?.cash_to_suppliers_formatted || '$0.00', ''],
    ['Employee Salaries', data.operating_activities?.employee_salaries_formatted || '$0.00', ''],
    ['Rent Payments', data.operating_activities?.rent_payments_formatted || '$0.00', ''],
    ['Utilities', data.operating_activities?.utilities_formatted || '$0.00', ''],
    ['Net Operating Cash Flow', data.operating_activities?.net_operating_cash_flow_formatted || '$0.00', ''],
    ['', '', ''],
    ['INVESTING ACTIVITIES', '', ''],
    ['Equipment Purchases', data.investing_activities?.equipment_purchases_formatted || '$0.00', ''],
    ['Asset Sales', data.investing_activities?.asset_sales_formatted || '$0.00', ''],
    ['Net Investing Cash Flow', data.investing_activities?.net_investing_cash_flow_formatted || '$0.00', ''],
    ['', '', ''],
    ['FINANCING ACTIVITIES', '', ''],
    ['Loan Proceeds', data.financing_activities?.loan_proceeds_formatted || '$0.00', ''],
    ['Loan Repayments', data.financing_activities?.loan_repayments_formatted || '$0.00', ''],
    ['Net Financing Cash Flow', data.financing_activities?.net_financing_cash_flow_formatted || '$0.00', ''],
    ['', '', ''],
    ['CASH SUMMARY', '', ''],
    ['Net Change in Cash', data.net_change_in_cash_formatted || '$0.00', ''],
    ['Opening Cash Balance', data.opening_cash_balance_formatted || '$0.00', ''],
    ['Closing Cash Balance', data.closing_cash_balance_formatted || '$0.00', '']
  ];

  doc.autoTable({
    startY: startY,
    head: [['Description', 'Amount', 'Notes']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [41, 128, 185] },
    styles: { fontSize: 10 }
  });

  return doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 20 : startY + 100;
};

// Aged Debtors PDF Export
const exportAgedDebtorsToPDF = (doc: jsPDF, data: any, startY: number): number => {
  const customers = data.customers || [];
  const summary = data.summary || {};

  // Summary section
  const summaryData = [
    ['Total Customers', summary.total_customers?.toString() || '0', ''],
    ['Total Outstanding', summary.total_outstanding_formatted || '$0.00', ''],
    ['Current (0-30 days)', summary.total_current_formatted || '$0.00', ''],
    ['31-60 days', summary.total_31_60_formatted || '$0.00', ''],
    ['61-90 days', summary.total_61_90_formatted || '$0.00', ''],
    ['Over 90 days', summary.total_over_90_formatted || '$0.00', ''],
    ['High Risk Customers', summary.high_risk_customers?.toString() || '0', '']
  ];

  doc.autoTable({
    startY: startY,
    head: [['Summary', 'Amount', 'Notes']],
    body: summaryData,
    theme: 'striped',
    headStyles: { fillColor: [41, 128, 185] },
    styles: { fontSize: 10 }
  });

  let currentY = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 20 : startY + 100;

  // Customer details
  if (customers.length > 0) {
    const customerData = customers.map((customer: any) => [
      customer.customer_name || '',
      customer.total_outstanding_formatted || '$0.00',
      customer.current_formatted || '$0.00',
      customer.days_31_60_formatted || '$0.00',
      customer.days_61_90_formatted || '$0.00',
      customer.days_over_90_formatted || '$0.00',
      customer.risk_level || 'Low'
    ]);

    doc.autoTable({
      startY: currentY,
      head: [['Customer', 'Total', 'Current', '31-60', '61-90', '90+', 'Risk']],
      body: customerData,
      theme: 'striped',
      headStyles: { fillColor: [41, 128, 185] },
      styles: { fontSize: 9 }
    });

    currentY = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 20 : currentY + 100;
  }

  return currentY;
};

// Excel Export Functions
export const exportToExcel = (reportData: ReportData): void => {
  const workbook = XLSX.utils.book_new();

  switch (reportData.type) {
    case 'profit-loss':
      exportProfitLossToExcel(workbook, reportData);
      break;
    case 'balance-sheet':
      exportBalanceSheetToExcel(workbook, reportData);
      break;
    case 'cash-flow':
      exportCashFlowToExcel(workbook, reportData);
      break;
    case 'aged-debtors':
      exportAgedDebtorsToExcel(workbook, reportData);
      break;
  }

  const fileName = `${reportData.title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(workbook, fileName);
};

const exportProfitLossToExcel = (workbook: XLSX.WorkBook, reportData: ReportData): void => {
  const data = reportData.data;
  const worksheetData = [
    [reportData.title],
    [reportData.period],
    [],
    ['REVENUE'],
    ['Sales Revenue', data.revenue?.sales_revenue_formatted || '$0.00'],
    ['Other Income', data.revenue?.other_income_formatted || '$0.00'],
    ['Total Revenue', data.revenue?.total_revenue_formatted || '$0.00'],
    [],
    ['EXPENSES'],
    ['Cost of Sales', data.expenses?.cost_of_sales_formatted || '$0.00'],
    ['Operating Expenses', data.expenses?.operating_expenses_formatted || '$0.00'],
    ['Administrative Expenses', data.expenses?.administrative_expenses_formatted || '$0.00'],
    ['Total Expenses', data.expenses?.total_expenses_formatted || '$0.00'],
    [],
    ['PROFIT SUMMARY'],
    ['Gross Profit', data.gross_profit_formatted || '$0.00'],
    ['Net Profit', data.net_profit_formatted || '$0.00'],
    ['Gross Margin', `${data.gross_margin?.toFixed(1) || '0.0'}%`],
    ['Net Margin', `${data.net_margin?.toFixed(1) || '0.0'}%`]
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Profit & Loss');
};

const exportBalanceSheetToExcel = (workbook: XLSX.WorkBook, reportData: ReportData): void => {
  const data = reportData.data;
  const worksheetData = [
    [reportData.title],
    [reportData.period],
    [],
    ['ASSETS'],
    ['Current Assets:'],
    ['Cash and Bank', data.current_assets?.cash_and_bank_formatted || '$0.00'],
    ['Accounts Receivable', data.current_assets?.accounts_receivable_formatted || '$0.00'],
    ['Inventory', data.current_assets?.inventory_formatted || '$0.00'],
    ['Total Current Assets', data.current_assets?.total_current_assets_formatted || '$0.00'],
    [],
    ['Fixed Assets:'],
    ['Property, Plant & Equipment', data.fixed_assets?.property_plant_equipment_formatted || '$0.00'],
    ['Less: Accumulated Depreciation', data.fixed_assets?.accumulated_depreciation_formatted || '$0.00'],
    ['Total Fixed Assets', data.fixed_assets?.total_fixed_assets_formatted || '$0.00'],
    ['TOTAL ASSETS', data.total_assets_formatted || '$0.00'],
    [],
    ['LIABILITIES & EQUITY'],
    ['Current Liabilities:'],
    ['Accounts Payable', data.current_liabilities?.accounts_payable_formatted || '$0.00'],
    ['Accrued Expenses', data.current_liabilities?.accrued_expenses_formatted || '$0.00'],
    ['Total Current Liabilities', data.current_liabilities?.total_current_liabilities_formatted || '$0.00'],
    [],
    ['Long-term Liabilities:'],
    ['Long-term Debt', data.long_term_liabilities?.long_term_debt_formatted || '$0.00'],
    ['Total Liabilities', data.total_liabilities_formatted || '$0.00'],
    [],
    ['Equity:'],
    ['Share Capital', data.equity?.share_capital_formatted || '$0.00'],
    ['Retained Earnings', data.equity?.retained_earnings_formatted || '$0.00'],
    ['Total Equity', data.equity?.total_equity_formatted || '$0.00'],
    ['TOTAL LIABILITIES & EQUITY', data.total_liabilities_and_equity_formatted || '$0.00']
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Balance Sheet');
};

const exportCashFlowToExcel = (workbook: XLSX.WorkBook, reportData: ReportData): void => {
  const data = reportData.data;
  const worksheetData = [
    [reportData.title],
    [reportData.period],
    [],
    ['OPERATING ACTIVITIES'],
    ['Cash from Customers', data.operating_activities?.cash_from_customers_formatted || '$0.00'],
    ['Cash to Suppliers', data.operating_activities?.cash_to_suppliers_formatted || '$0.00'],
    ['Employee Salaries', data.operating_activities?.employee_salaries_formatted || '$0.00'],
    ['Rent Payments', data.operating_activities?.rent_payments_formatted || '$0.00'],
    ['Utilities', data.operating_activities?.utilities_formatted || '$0.00'],
    ['Net Operating Cash Flow', data.operating_activities?.net_operating_cash_flow_formatted || '$0.00'],
    [],
    ['INVESTING ACTIVITIES'],
    ['Equipment Purchases', data.investing_activities?.equipment_purchases_formatted || '$0.00'],
    ['Asset Sales', data.investing_activities?.asset_sales_formatted || '$0.00'],
    ['Net Investing Cash Flow', data.investing_activities?.net_investing_cash_flow_formatted || '$0.00'],
    [],
    ['FINANCING ACTIVITIES'],
    ['Loan Proceeds', data.financing_activities?.loan_proceeds_formatted || '$0.00'],
    ['Loan Repayments', data.financing_activities?.loan_repayments_formatted || '$0.00'],
    ['Net Financing Cash Flow', data.financing_activities?.net_financing_cash_flow_formatted || '$0.00'],
    [],
    ['CASH SUMMARY'],
    ['Net Change in Cash', data.net_change_in_cash_formatted || '$0.00'],
    ['Opening Cash Balance', data.opening_cash_balance_formatted || '$0.00'],
    ['Closing Cash Balance', data.closing_cash_balance_formatted || '$0.00']
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Cash Flow');
};

const exportAgedDebtorsToExcel = (workbook: XLSX.WorkBook, reportData: ReportData): void => {
  const data = reportData.data;
  const customers = data.customers || [];
  const summary = data.summary || {};

  // Summary sheet
  const summaryData = [
    [reportData.title],
    [reportData.period],
    [],
    ['SUMMARY'],
    ['Total Customers', summary.total_customers?.toString() || '0'],
    ['Total Outstanding', summary.total_outstanding_formatted || '$0.00'],
    ['Current (0-30 days)', summary.total_current_formatted || '$0.00'],
    ['31-60 days', summary.total_31_60_formatted || '$0.00'],
    ['61-90 days', summary.total_61_90_formatted || '$0.00'],
    ['Over 90 days', summary.total_over_90_formatted || '$0.00'],
    ['High Risk Customers', summary.high_risk_customers?.toString() || '0']
  ];

  const summaryWorksheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');

  // Customer details sheet
  if (customers.length > 0) {
    const customerData = [
      ['Customer', 'Total Outstanding', 'Current', '31-60 Days', '61-90 Days', '90+ Days', 'Risk Level', 'Days Overdue'],
      ...customers.map((customer: any) => [
        customer.customer_name || '',
        customer.total_outstanding_formatted || '$0.00',
        customer.current_formatted || '$0.00',
        customer.days_31_60_formatted || '$0.00',
        customer.days_61_90_formatted || '$0.00',
        customer.days_over_90_formatted || '$0.00',
        customer.risk_level || 'Low',
        customer.days_overdue?.toString() || '0'
      ])
    ];

    const customerWorksheet = XLSX.utils.aoa_to_sheet(customerData);
    XLSX.utils.book_append_sheet(workbook, customerWorksheet, 'Customer Details');
  }
};

// CSV Export Functions
export const exportToCSV = (reportData: ReportData): void => {
  let csvContent = '';

  switch (reportData.type) {
    case 'profit-loss':
      csvContent = exportProfitLossToCSV(reportData);
      break;
    case 'balance-sheet':
      csvContent = exportBalanceSheetToCSV(reportData);
      break;
    case 'cash-flow':
      csvContent = exportCashFlowToCSV(reportData);
      break;
    case 'aged-debtors':
      csvContent = exportAgedDebtorsToCSV(reportData);
      break;
  }

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const fileName = `${reportData.title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
  saveAs(blob, fileName);
};

const exportProfitLossToCSV = (reportData: ReportData): string => {
  const data = reportData.data;
  const rows = [
    [reportData.title],
    [reportData.period],
    [],
    ['REVENUE'],
    ['Sales Revenue', data.revenue?.sales_revenue_formatted || '$0.00'],
    ['Other Income', data.revenue?.other_income_formatted || '$0.00'],
    ['Total Revenue', data.revenue?.total_revenue_formatted || '$0.00'],
    [],
    ['EXPENSES'],
    ['Cost of Sales', data.expenses?.cost_of_sales_formatted || '$0.00'],
    ['Operating Expenses', data.expenses?.operating_expenses_formatted || '$0.00'],
    ['Administrative Expenses', data.expenses?.administrative_expenses_formatted || '$0.00'],
    ['Total Expenses', data.expenses?.total_expenses_formatted || '$0.00'],
    [],
    ['PROFIT SUMMARY'],
    ['Gross Profit', data.gross_profit_formatted || '$0.00'],
    ['Net Profit', data.net_profit_formatted || '$0.00'],
    ['Gross Margin', `${data.gross_margin?.toFixed(1) || '0.0'}%`],
    ['Net Margin', `${data.net_margin?.toFixed(1) || '0.0'}%`]
  ];

  return rows.map(row => row.join(',')).join('\n');
};

const exportBalanceSheetToCSV = (reportData: ReportData): string => {
  const data = reportData.data;
  const rows = [
    [reportData.title],
    [reportData.period],
    [],
    ['ASSETS'],
    ['Current Assets:'],
    ['Cash and Bank', data.current_assets?.cash_and_bank_formatted || '$0.00'],
    ['Accounts Receivable', data.current_assets?.accounts_receivable_formatted || '$0.00'],
    ['Inventory', data.current_assets?.inventory_formatted || '$0.00'],
    ['Total Current Assets', data.current_assets?.total_current_assets_formatted || '$0.00'],
    [],
    ['Fixed Assets:'],
    ['Property, Plant & Equipment', data.fixed_assets?.property_plant_equipment_formatted || '$0.00'],
    ['Less: Accumulated Depreciation', data.fixed_assets?.accumulated_depreciation_formatted || '$0.00'],
    ['Total Fixed Assets', data.fixed_assets?.total_fixed_assets_formatted || '$0.00'],
    ['TOTAL ASSETS', data.total_assets_formatted || '$0.00'],
    [],
    ['LIABILITIES & EQUITY'],
    ['Current Liabilities:'],
    ['Accounts Payable', data.current_liabilities?.accounts_payable_formatted || '$0.00'],
    ['Accrued Expenses', data.current_liabilities?.accrued_expenses_formatted || '$0.00'],
    ['Total Current Liabilities', data.current_liabilities?.total_current_liabilities_formatted || '$0.00'],
    [],
    ['Long-term Liabilities:'],
    ['Long-term Debt', data.long_term_liabilities?.long_term_debt_formatted || '$0.00'],
    ['Total Liabilities', data.total_liabilities_formatted || '$0.00'],
    [],
    ['Equity:'],
    ['Share Capital', data.equity?.share_capital_formatted || '$0.00'],
    ['Retained Earnings', data.equity?.retained_earnings_formatted || '$0.00'],
    ['Total Equity', data.equity?.total_equity_formatted || '$0.00'],
    ['TOTAL LIABILITIES & EQUITY', data.total_liabilities_and_equity_formatted || '$0.00']
  ];

  return rows.map(row => row.join(',')).join('\n');
};

const exportCashFlowToCSV = (reportData: ReportData): string => {
  const data = reportData.data;
  const rows = [
    [reportData.title],
    [reportData.period],
    [],
    ['OPERATING ACTIVITIES'],
    ['Cash from Customers', data.operating_activities?.cash_from_customers_formatted || '$0.00'],
    ['Cash to Suppliers', data.operating_activities?.cash_to_suppliers_formatted || '$0.00'],
    ['Employee Salaries', data.operating_activities?.employee_salaries_formatted || '$0.00'],
    ['Rent Payments', data.operating_activities?.rent_payments_formatted || '$0.00'],
    ['Utilities', data.operating_activities?.utilities_formatted || '$0.00'],
    ['Net Operating Cash Flow', data.operating_activities?.net_operating_cash_flow_formatted || '$0.00'],
    [],
    ['INVESTING ACTIVITIES'],
    ['Equipment Purchases', data.investing_activities?.equipment_purchases_formatted || '$0.00'],
    ['Asset Sales', data.investing_activities?.asset_sales_formatted || '$0.00'],
    ['Net Investing Cash Flow', data.investing_activities?.net_investing_cash_flow_formatted || '$0.00'],
    [],
    ['FINANCING ACTIVITIES'],
    ['Loan Proceeds', data.financing_activities?.loan_proceeds_formatted || '$0.00'],
    ['Loan Repayments', data.financing_activities?.loan_repayments_formatted || '$0.00'],
    ['Net Financing Cash Flow', data.financing_activities?.net_financing_cash_flow_formatted || '$0.00'],
    [],
    ['CASH SUMMARY'],
    ['Net Change in Cash', data.net_change_in_cash_formatted || '$0.00'],
    ['Opening Cash Balance', data.opening_cash_balance_formatted || '$0.00'],
    ['Closing Cash Balance', data.closing_cash_balance_formatted || '$0.00']
  ];

  return rows.map(row => row.join(',')).join('\n');
};

const exportAgedDebtorsToCSV = (reportData: ReportData): string => {
  const data = reportData.data;
  const customers = data.customers || [];
  const summary = data.summary || {};

  const rows = [
    [reportData.title],
    [reportData.period],
    [],
    ['SUMMARY'],
    ['Total Customers', summary.total_customers?.toString() || '0'],
    ['Total Outstanding', summary.total_outstanding_formatted || '$0.00'],
    ['Current (0-30 days)', summary.total_current_formatted || '$0.00'],
    ['31-60 days', summary.total_31_60_formatted || '$0.00'],
    ['61-90 days', summary.total_61_90_formatted || '$0.00'],
    ['Over 90 days', summary.total_over_90_formatted || '$0.00'],
    ['High Risk Customers', summary.high_risk_customers?.toString() || '0'],
    [],
    ['CUSTOMER DETAILS'],
    ['Customer', 'Total Outstanding', 'Current', '31-60 Days', '61-90 Days', '90+ Days', 'Risk Level', 'Days Overdue'],
    ...customers.map((customer: any) => [
      customer.customer_name || '',
      customer.total_outstanding_formatted || '$0.00',
      customer.current_formatted || '$0.00',
      customer.days_31_60_formatted || '$0.00',
      customer.days_61_90_formatted || '$0.00',
      customer.days_over_90_formatted || '$0.00',
      customer.risk_level || 'Low',
      customer.days_overdue?.toString() || '0'
    ])
  ];

  return rows.map(row => row.join(',')).join('\n');
};
